import {
  Backdrop,
  Box,
  Container,
  LinearProgress,
  Portal,
  Skeleton,
  Stack,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { LogoText } from '../../assets/logo-text';
import { useMuiSwitchableTheme } from '../../theme';

const delayedLoadingWrapper =
  (Component: React.FC) =>
  // eslint-disable-next-line react/display-name
  ({ delay = 0 }: { delay?: number }) => {
    const [show, setShow] = useState(delay === 0);

    useEffect(() => {
      const timer = setTimeout(() => setShow(true), delay);
      return () => clearTimeout(timer);
    }, [delay]);

    if (!show) return null;

    return <Component />;
  };

// Widths for the generic page's placeholder rows, uneven so they read as
// records rather than stripes.
const LOADING_ROW_WIDTHS = ['62%', '48%', '70%', '55%', '40%'];

/**
 * The generic page-loading state, for pages with no dedicated skeleton. Shaped
 * like the app's pages rather than as bare grey blocks: a panel with the tinted
 * header bar (title, description, action) that SearchPage and the detail pages
 * wear, then a row of summary cards and a short list.
 */
export const Loading = delayedLoadingWrapper(() => (
  <Container sx={{ width: 1, px: { xs: 0, md: 2 } }} aria-busy>
    <Box
      sx={{
        mt: 1,
        bgcolor: 'background.paper',
        borderRadius: { xs: 0, md: 2 },
        overflow: 'hidden',
      }}
    >
      {/* Header bar */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          bgcolor: 'action.hover',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton width={200} height={30} sx={{ maxWidth: '70%' }} />
          <Skeleton width="45%" height={20} />
        </Box>
        <Skeleton variant="rounded" width={96} height={36} />
      </Stack>

      <Stack spacing={3} sx={{ p: { xs: 2, md: 3 } }}>
        {/* Summary cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {[0, 1, 2].map((card) => (
            <Box
              key={card}
              sx={{
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                // One card is enough on a phone; three stacked would push the
                // list off the screen.
                display: { xs: card === 0 ? 'block' : 'none', sm: 'block' },
              }}
            >
              <Skeleton width="50%" height={18} />
              <Skeleton width="35%" height={36} />
            </Box>
          ))}
        </Box>

        {/* List */}
        <Box>
          <Skeleton width={160} height={26} sx={{ mb: 1 }} />
          {LOADING_ROW_WIDTHS.map((width) => (
            <Stack
              key={width}
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ height: 52, borderBottom: 1, borderColor: 'divider' }}
            >
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton width={width} height={22} />
              <Box sx={{ flex: 1 }} />
              <Skeleton variant="rounded" width={64} height={24} />
            </Stack>
          ))}
        </Box>
      </Stack>
    </Box>
  </Container>
));

export const BlockLoading = delayedLoadingWrapper(() => (
  <Stack spacing={2} direction="column" component={Container} sx={{ py: 2 }}>
    <Skeleton variant="rectangular" height={200} />
    <Skeleton variant="rectangular" height={75} />
    <Skeleton variant="rectangular" height={75} />
  </Stack>
));

export const ColumnLoading = delayedLoadingWrapper(() => (
  <Stack
    spacing={2}
    direction={{ xs: 'column', md: 'row' }}
    sx={{ p: 2 }}
    component={Container}
  >
    <Skeleton
      variant="rectangular"
      height="80vh"
      sx={{ width: { xs: 1, md: 0.3 } }}
    />
    <Skeleton
      variant="rectangular"
      height="80vh"
      width="70%"
      sx={{ display: { xs: 'none', md: 'flex' } }}
    />
  </Stack>
));

export const FooterLoading = delayedLoadingWrapper(() => {
  const { marginLeft } = useMuiSwitchableTheme();
  return (
    <Portal>
      <LinearProgress
        sx={{
          position: 'fixed',
          bottom: 10,
          left: marginLeft + 10,
          right: 10,
          zIndex: 1000,
          borderRadius: 1,
          height: 6,
        }}
      />
    </Portal>
  );
});

// The wordmark's letters lift one after another, f through e, then the whole
// mark rests before the next pass. Something is always visibly moving, so a
// slow load never looks frozen. LogoText draws one path per letter, which is
// what the per-letter delay hangs off. index.html's boot shell draws the same
// thing before React loads; keep the two in step.
const LOADING_LETTER_COUNT = 9;
const LOADING_LETTER_STAGGER_MS = 85;
const LOADING_WAVE_MS = 1800;

/**
 * How far into its loop an animation of `periodMs` would be had it started
 * when the page loaded.
 *
 * FullscreenLoading is the fallback for several nested Suspense boundaries, so
 * one load hands it from instance to instance. A fresh instance starting its
 * animations at zero made every handover visibly restart the wave. Starting
 * each one at this offset (as a negative delay) puts every instance, and the
 * index.html boot shell, on the same clock, so a handover is invisible.
 * @param periodMs - The animation's full cycle
 * @returns Milliseconds into the cycle, right now
 */
function loopPhase(periodMs: number): number {
  const now = typeof performance === 'undefined' ? 0 : performance.now();
  return now % periodMs;
}

function letterDelays(phaseMs: number) {
  return Object.fromEntries(
    Array.from({ length: LOADING_LETTER_COUNT }, (_, idx) => [
      `& path:nth-of-type(${idx + 1})`,
      { animationDelay: `${idx * LOADING_LETTER_STAGGER_MS - phaseMs}ms` },
    ]),
  );
}

// Behind the wordmark, a wash of Farmshare colours (the backdrop sage, the
// primary green, a deeper green, a muted harvest gold) drifts back and forth,
// one direction per two letter waves, so the background and the mark move to
// the same beat. The mark is cream: the brand's dark green almost disappears
// on these greens (under 2:1). The same colours in dark mode: the loader is a
// brand moment, not a page, and index.html's boot shell can't know the theme.
const LOADING_GRADIENT_MS = LOADING_WAVE_MS * 2;
const LOADING_GRADIENT = [
  '#6a8e7f',
  '#5b8571',
  '#4c735f',
  '#8a7f5c',
  '#6a8e7f',
];
const LOADING_MARK_COLOR = '#f6f2ee';

export const FullscreenLoading = delayedLoadingWrapper(() => {
  const theme = useTheme();
  // Fixed at mount: see loopPhase. The gradient alternates, so its full cycle
  // is there and back.
  const [phase] = useState(() => ({
    wave: loopPhase(LOADING_WAVE_MS),
    gradient: loopPhase(LOADING_GRADIENT_MS * 2),
  }));

  return (
    <Backdrop
      open
      // No fade-in: a fallback handing over to the next one would otherwise
      // flash the page through the overlay while the new one fades up.
      transitionDuration={0}
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        display: 'flex',
        top: 0,
        backgroundImage: `linear-gradient(120deg, ${LOADING_GRADIENT.join(', ')})`,
        backgroundColor: LOADING_GRADIENT[0],
        // Only the height overflows, so the drift runs top to bottom; a
        // horizontal position change on a 100%-wide background moves nothing.
        backgroundSize: '100% 200%',
        '@keyframes loading-gradient': {
          from: { backgroundPosition: '50% 0%' },
          to: { backgroundPosition: '50% 100%' },
        },
        animation: `loading-gradient ${LOADING_GRADIENT_MS}ms ease-in-out ${-phase.gradient}ms infinite alternate`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <Box role="status" aria-label="Loading">
        <LogoText
          aria-hidden
          sx={{
            color: LOADING_MARK_COLOR,
            // Phone size by default, then larger on desktop, where 280px reads
            // as small in the middle of a wide screen.
            width: { xs: 280, md: 440, lg: 560 },
            maxWidth: '80vw',
            lineHeight: 0,
            // The tall letters (f, h) rise past the top of the viewBox, which
            // an inline svg clips by default.
            '& svg': { overflow: 'visible' },
            '@keyframes letter-wave': {
              '0%, 45%, 100%': { transform: 'translateY(0)', opacity: 1 },
              '18%': { transform: 'translateY(-14%)', opacity: 0.55 },
            },
            '& path': {
              transformBox: 'fill-box',
              transformOrigin: '50% 100%',
              animation: `letter-wave ${LOADING_WAVE_MS}ms cubic-bezier(0.45, 0, 0.25, 1) infinite`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            },
            ...letterDelays(phase.wave),
          }}
        />
      </Box>
    </Backdrop>
  );
});

/**
 * One placeholder row shaped like a mobile agenda job card: three lines
 * (customer, animal count, flags) beside the status chip, 87px tall. The
 * calendar's own agenda skeleton takes over from this one, so the two and the
 * real card have to agree on height or the list steps as they hand over.
 */
const MobileJobRowSkeleton = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      py: '12px',
      px: 1,
      mb: 1,
      borderRadius: 1,
      bgcolor: 'background.paper',
    }}
  >
    {/* The disclosure button's gutter. */}
    <Box sx={{ width: 38, flexShrink: 0 }} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Skeleton variant="text" width="60%" height={19} />
      <Skeleton variant="text" width="38%" height={19} sx={{ mt: '2px' }} />
      <Box
        sx={{ height: 20, mt: '3px', display: 'flex', alignItems: 'center' }}
      >
        <Skeleton variant="circular" width={16} height={16} />
      </Box>
    </Box>
    <Skeleton
      variant="rounded"
      width={78}
      height={22}
      sx={{ borderRadius: 999 }}
    />
  </Box>
);

export const CalendarLoading = delayedLoadingWrapper(() => (
  // No spacing on phones: `spacing` puts a top margin on the second child even
  // when the first (the desktop grid) is `display: none`, which sat the mobile
  // skeleton 16px lower than the calendar that replaces it.
  <Stack
    spacing={{ xs: 0, md: 2 }}
    sx={{ p: { xs: 1, md: 2 } }}
    component={Container}
  >
    {/* Desktop / tablet: the month grid.

        `xs: 'none'`, not `xs: 'block'` — the two branches used to be
        `block`/`none`, so the seven-column grid rendered on a phone as well as
        the mobile branch below it, and the loading state looked like the
        desktop calendar regardless of screen. */}
    <Stack
      sx={{ width: 1, display: { xs: 'none', md: 'flex' } }}
      direction="column"
      spacing={2}
    >
      <Skeleton variant="rectangular" height={50} sx={{ width: 1 }} />
      <Stack
        spacing={2}
        direction="row"
        sx={{ width: 1, mb: 4 }}
        justifyContent="space-between"
      >
        <Skeleton variant="rectangular" height={40} sx={{ width: 0.3 }} />
        <Skeleton variant="rectangular" height={40} sx={{ width: 0.3 }} />
      </Stack>

      <Stack spacing={1} direction="row" justifyContent="space-evenly">
        {Array.from(Array(7)).map((_, idx) => (
          <Skeleton
            key={`header-${idx}`}
            variant="rectangular"
            height={20}
            sx={{ mx: 0.5, flex: 1 }}
          />
        ))}
      </Stack>

      {Array.from(Array(5)).map((_, rowIdx) => (
        <Stack key={`row-${rowIdx}`} spacing={1} direction="row" mt={1}>
          {Array.from(Array(7)).map((_, colIdx) => (
            <Skeleton
              key={`cell-${rowIdx}-${colIdx}`}
              variant="rectangular"
              height={160}
              sx={{ mx: 0.5, flex: 1 }}
            />
          ))}
        </Stack>
      ))}
    </Stack>

    {/* Mobile: date container, filter row, then the day's agenda — the shape
        the mobile calendar actually renders, so nothing jumps when it lands. */}
    <Stack sx={{ width: 1, display: { xs: 'block', md: 'none' } }}>
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            py: 1,
            // The real date header's height, so the rows below don't move.
            minHeight: 49,
          }}
        >
          <Skeleton variant="text" width={150} height={24} />
          <Stack direction="row" spacing={0.5}>
            <Skeleton variant="circular" width={28} height={28} />
            <Skeleton variant="circular" width={28} height={28} />
          </Stack>
        </Box>
        {/* The capacity rail. */}
        <LinearProgress sx={{ height: 3 }} />
      </Box>

      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <Skeleton variant="rounded" height={41} sx={{ flex: 1 }} />
        <Skeleton variant="circular" width={40} height={40} />
      </Stack>

      {[0, 1].map((section) => (
        <Box key={`mobile-section-${section}`} sx={{ mb: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Skeleton variant="circular" width={8} height={8} />
            <Skeleton variant="text" width={72} height={14} />
          </Stack>
          {Array.from(Array(section === 0 ? 2 : 1)).map((_, row) => (
            <MobileJobRowSkeleton key={`mobile-row-${section}-${row}`} />
          ))}
        </Box>
      ))}
    </Stack>
  </Stack>
));
